/**
 * Contexte Socket.io global pour la PWA.
 * - Une seule connexion par session, authentifiée par token.
 * - Rejoint automatiquement la room user:{userId} pour recevoir badge:update.
 * - Expose le socket pour que ProjectConversation puisse émettre join:project / leave:project.
 */

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { getWebSocketUrl } from '../utils/env.js';
import axiosInstance from '../config/axios';
import { AppContext } from './AppContext';
import { ensureOsNotificationPermission, showOsNotification } from '../utils/osNotifications.js';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const appContext = useContext(AppContext);
  const isLoggedIn = !!appContext?.isLoggedIn;
  const [socket, setSocket] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  const baseTitleRef = useRef(null);
  const refreshUnreadTotal = async () => {
    try {
      const res = await axiosInstance.get('/project-messages/unread-total');
      if (res.data?.success && typeof res.data.unreadTotal === 'number') {
        const count = res.data.unreadTotal;
        setUnreadTotal(count);
        if (navigator.setAppBadge) {
          if (count > 0) {
            navigator.setAppBadge(count).catch(() => {});
          } else {
            navigator.clearAppBadge?.().catch(() => {});
          }
        }
        if (!baseTitleRef.current) baseTitleRef.current = document.title.replace(/^\(\d+\)\s*/, '') || 'Secura';
        document.title = count > 0 ? `(${count}) ${baseTitleRef.current}` : baseTitleRef.current;
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = getWebSocketUrl();
    const s = io(wsUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });
    socketRef.current = s;
    setSocket(s);

    s.on('connect', () => {
      setConnected(true);
      refreshUnreadTotal();
      void ensureOsNotificationPermission();
    });
    s.on('disconnect', () => setConnected(false));
    s.on('badge:update', () => {
      refreshUnreadTotal();
    });
    s.on('notification:push', (payload) => {
      const title = payload?.title || 'Secura';
      const body = payload?.body || '';
      const url = payload?.data?.link || payload?.link;
      const type = payload?.data?.type || payload?.type || '';
      const projetId = payload?.data?.projetId || payload?.projetId;
      const onSameProject =
        projetId &&
        typeof window !== 'undefined' &&
        window.location.pathname.includes(`/projets/${projetId}`) &&
        !document.hidden;
      if (onSameProject && type === 'project_message') return;
      showOsNotification({
        title,
        body,
        url,
        tag: `secura-${type}-${projetId || Date.now()}`,
      });
      refreshUnreadTotal();
    });

    return () => {
      s.removeAllListeners();
      s.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnected(false);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    if (!connected) return;
    refreshUnreadTotal();
  }, [connected]);

  const value = {
    socket,
    connected,
    unreadTotal,
    refreshUnreadTotal,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
}

/** Utiliser le socket sans exiger SocketProvider (retourne socket null si absent). */
export function useSocketOptional() {
  const ctx = useContext(SocketContext);
  return ctx || { socket: null, connected: false, unreadTotal: 0, refreshUnreadTotal: () => {} };
}
